import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore, useTenantsStore } from "@/stores";
import type IItem from "@/models/responses/IItem";
import type { IItemOpenOrder, IItemOpenQuantityEntry } from "@/models/responses/IItemOpenOrder";
import Item from "@/models/frontend/Item";
import { CreateItemRequest } from "@/models/requests/CreateItemRequest";
import { UpdateItemRequest } from "@/models/requests/UpdateItemRequest";

export const useItemsStore = defineStore("items", {
  state: () => ({
    error: "" as any,
    data: [] as Item[],
    loadedTenantId: 0,
    isLoading: false,
    loadCompleted: false,
    openOrdersQuantityByItemId: {} as Record<number, IItemOpenQuantityEntry[]>,
  }),
  actions: {
    async fetchAll(force = false, resolvedFunction?: () => Promise<void>) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedTenantId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedTenantId === selectedTenantId.value) {
        console.debug("Items already loaded, skipping fetch");
        resolvedFunction && await resolvedFunction();
        return;
      }

      this.isLoading = true;
      this.loadCompleted = false;
      this.error = "";
      console.debug(`Fetching items for tenant ${selectedTenantId.value}`);
      
      // await api
      //   .get<IItem[]>(`v1.0/items?tenantId=${selectedTenantId.value}`)
      //   .then((response) => {
      //     this.data = response.data.map((item) => this.mapToFrontend(item));
      //     this.loadedTenantId = selectedTenantId.value;
      //   })
      //   .catch((error) => (this.error = error));
      try {
        const response = await api.get<IItem[]>(`v1.0/items?tenantId=${selectedTenantId.value}`);
        console.debug(`Received ${response.data.length} items from API, processing...`);
        this.data = response.data.map((item) => this.mapToFrontend(item));
        this.loadedTenantId = selectedTenantId.value;
        this.loadCompleted = true;
        this.isLoading = false;
        console.debug(`Items processing complete, ${this.data.length} items mapped and ready`);
        resolvedFunction && await resolvedFunction();
    } catch (error) {
        this.error = error;
        this.isLoading = false;
        console.error("Error loading items:", error);
      }
    },
    async create(body: CreateItemRequest) : Promise<number> {
      this.error = "";
      let affectedId = 0;
      await api
        .post<IItem>(`v1.0/items`, body)
        .then((response) => {
          const itemResponse = response.data;
          if (itemResponse) {
            affectedId = itemResponse.id;
            const dataIndex = this.data.findIndex(
              (item) => item.id === itemResponse.id
            );
            if (dataIndex > -1) {
              this.data[dataIndex] = this.mapToFrontend(itemResponse);
            } else {
              this.data.push(this.mapToFrontend(itemResponse));
            }
          }
        })
        .catch((error) => {
          if (error.response) {
            // Server responded with an error status code (4xx or 5xx)
            const problem = error.response.data;
    
            if (problem.type === 'https://tools.ietf.org/html/rfc7231#section-6.5.1'
              || problem.type === 'https://tools.ietf.org/html/rfc9110#section-15.6.1'
            ) {
              // invalid operation error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.detail;
            } else {
              // unexpected error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.title + ': ' + problem.detail;
            }
          } else if (error.request) {
            // no response received
            console.error('No response from server:', error.request);
            this.error = 'Server did not respond. Please try again later.';
          } else {
            // other errors
            console.error('Axios error:', error.message);
            this.error = 'Unexpected error: ' + error.message;
      }});
      return affectedId;
    },
    async update(id: number, body: UpdateItemRequest) {
      this.error = "";
      await api
        .put<IItem>(`v1.0/items/${id}`, body)
        .then((response) => {
          const itemResponse = response.data;
          if (itemResponse) {
            const dataIndex = this.data.findIndex(
              (item) => item.id === itemResponse.id
            );
            if (dataIndex > -1) {
              this.data[dataIndex] = this.mapToFrontend(itemResponse);
            } else {
              this.data.push(this.mapToFrontend(itemResponse));
            }
          }
        })
        .catch((error) => (this.error = error));
    },
    async fetchOpenOrders(itemId: number, siteId?: number): Promise<IItemOpenOrder[]> {
      const sessionStore = useSessionStore();
      const effectiveSiteId = siteId ?? sessionStore.selectedSiteId;
      const query = effectiveSiteId ? `?siteId=${effectiveSiteId}` : "";
      const response = await api.get<IItemOpenOrder[]>(`v1.0/items/${itemId}/open-orders${query}`);
      return response.data;
    },
    async fetchOpenOrdersQuantity(siteId?: number): Promise<void> {
      const sessionStore = useSessionStore();
      const effectiveSiteId = siteId ?? sessionStore.selectedSiteId;
      const query = effectiveSiteId ? `?siteId=${effectiveSiteId}` : "";
      const response = await api.get<Record<string, IItemOpenQuantityEntry[]>>(`v1.0/items/.open-orders-quantity${query}`);
      const result: Record<number, IItemOpenQuantityEntry[]> = {};
      for (const [key, value] of Object.entries(response.data)) {
        result[Number(key)] = value;
      }
      this.openOrdersQuantityByItemId = result;
    },
    mapToFrontend(item: IItem) {
      const tenantsStore = useTenantsStore();
      const tenant = tenantsStore.getById(item.tenantId);
      return new Item(
        item.id,
        tenant,
        item.code,
        this.addBlanksAfterNonDigitCommaOrDot(item.name),
        this.addBlanksAfterNonDigitCommaOrDot(item.description),
        item.baseUnitCode,
        item.requiresLabel,
        item.restockPullType,
        item.trackBestBeforeDate,
        item.trackExpirationDate,
        item.trackLotNumber,
        item.bestBeforeLeadTime,
        item.expirationLeadTime,
        item.isInternalGenerated,
        item.isBillable,
        item.allowCalculatedLot
      );
    },
  },
  getters: {
    getById:
      (state) => (id: number): Item => {
        return <Item>state.data.find((item) => item.id === id);
      },
    getByCode:
      (state) => (code: string): Item => {
        return <Item>state.data.find((item) => item.code === code);
      },
    addBlanksAfterNonDigitCommaOrDot:
      () => (text: string): string => {
      if (!text) return '';
      return text.replace(/([.,])([^\d\s])/g, '$1 $2');
    },
  },
});
